import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPointsHistory, getLoyaltyBalance } from '../../api/loyalty';
import SecureStorage from '../../utils/secureStorage';
import { PointsTransaction, LoyaltyBalance } from '../../@types/loyalty';
import { format } from 'date-fns';
import { PATHS } from '../../routes/routes';
import CustomerTopNav from '../../components/customerPortal/CustomerTopNav';
import CustomerFooter from '../../components/customerPortal/CustomerFooter';
import '../../styles/customerPortal.css';

// Helper function to decode email from customer token
// function decodeEmailFromToken(bearerToken: string | null): string | null {
//     if (!bearerToken) return null;
//     const token = bearerToken.startsWith('Bearer ') ? bearerToken.slice(7) : bearerToken;
//     const parts = token.split('.');
//     if (parts.length < 2) return null;
//     try {
//         const payload = JSON.parse(atob(parts[1]));
//         const emailRaw = payload?.email ?? payload?.sub ?? null;
//         return emailRaw ? String(emailRaw).trim().toLowerCase() : null;
//     } catch {
//         return null;
//     }
// }

const PointsHistory = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [transactions, setTransactions] = useState<PointsTransaction[]>([]);
    const [loyaltyBalance, setLoyaltyBalance] = useState<LoyaltyBalance | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    // removed loadingMore; pagination now switches pages directly

    const pageSize = 20;

    // Get customer email from token
    // const customerEmail = useMemo(() => decodeEmailFromToken(SecureStorage.getCustomerToken()), []);

    useEffect(() => {
        // Auth is enforced by CustomerGuard; just load data
        fetchData();
    }, []);

    const fetchData = async (page: number = 1) => {
        try {
            if (page === 1) setLoading(true);

            if (!SecureStorage.isCustomerTokenValid() && page === 1) {
                setError('Customer not authenticated - please sign in again');
                return;
            }
            
            const [historyData, balanceData] = await Promise.all([
                getPointsHistory(page, pageSize),
                page === 1 ? getLoyaltyBalance() : Promise.resolve(null)
            ]);

            if (page === 1) {
                setTransactions(historyData.transactions);
                if (balanceData) setLoyaltyBalance(balanceData);
            } else {
                setTransactions(prev => [...prev, ...historyData.transactions]);
            }

            setTotalPages(historyData.pagination.totalPages);
            setCurrentPage(page);
        } catch (error: any) {
            setError(error.response?.data?.error || 'Failed to load points history');
        } finally {
            setLoading(false);
        }
    };

    // loadMore removed in favor of numbered pagination

    const getTransactionIcon = (type: 'EARNED' | 'REDEEMED') => {
        return type === 'EARNED' ? (
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
            </div>
        ) : (
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="points-history-page bg-gray-50 min-h-screen flex flex-col">
                <CustomerTopNav />
                <div className="flex-1 flex justify-center items-center p-6">
                    <div className="flex flex-col items-center gap-4">
                        <div className="loading loading-dots loading-lg"></div>
                        <p className="text-gray-500">Loading your points history...</p>
                    </div>
                </div>
                <CustomerFooter />
            </div>
        );
    }

    if (error) {
        return (
            <div className="points-history-page bg-gray-50 min-h-screen flex flex-col">
                <CustomerTopNav />
                <div className="flex-1 flex justify-center items-center p-6">
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <p className="text-red-500 text-lg font-medium mb-2">Failed to load points history</p>
                        <p className="text-gray-500 mb-4">{error}</p>
                        <button 
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
                <CustomerFooter />
            </div>
        );
    }

    // Light pagination (server-driven)
    const PaginationLight = ({ page, total, onChange }: { page: number; total: number; onChange: (p: number) => void }) => {
        if (total <= 1) return null;
        const go = (p: number) => onChange(Math.min(total, Math.max(1, p)));
        const pages: number[] = [];
        for (let i = 1; i <= total; i++) pages.push(i);
        return (
            <div className="flex justify-center">
                <div className="join">
                    <button className="join-item btn btn-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50" onClick={() => go(page - 1)} disabled={page === 1}>«</button>
                    {pages.map(p => (
                        <button
                            key={p}
                            className={`join-item btn btn-sm ${p === page ? 'bg-gray-100 text-gray-900 border border-gray-300' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                            onClick={() => go(p)}
                        >
                            {p}
                        </button>
                    ))}
                    <button className="join-item btn btn-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50" onClick={() => go(page + 1)} disabled={page === total}>»</button>
                </div>
            </div>
        );
    };

    return (
        <div className="points-history-page bg-gray-50 min-h-screen flex flex-col">
            {/* Header Section */}
            <CustomerTopNav />

            {/* Main Content */}
            <div className="p-6 flex-1">
                <div className="w-full">

                {/* Points Summary - Dashboard Style */}
                {loyaltyBalance && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Current Balance</h2>
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gray-50 rounded-xl p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                        </svg>
                                    </div>
                                    <div className="text-sm text-gray-500">Total Earned</div>
                                </div>
                                <div className="text-2xl font-bold text-gray-900 text-left">{loyaltyBalance.totalPoints.toLocaleString()}</div>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0 0v2m0 4v.01M4.93 4.93l1.414 1.414M17.657 17.657l1.414 1.414M2 12h2m16 0h2M4.93 19.07l1.414-1.414M17.657 6.343l1.414-1.414" />
                                        </svg>
                                    </div>
                                    <div className="text-sm text-gray-500">Available</div>
                                </div>
                                <div className="text-2xl font-bold text-gray-900 text-left">{loyaltyBalance.availablePoints.toLocaleString()}</div>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                                        </svg>
                                    </div>
                                    <div className="text-sm text-gray-500">Redeemed</div>
                                </div>
                                <div className="text-2xl font-bold text-gray-900 text-left">{loyaltyBalance.redeemedPoints.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Transactions List - Dashboard Style */}
                {transactions.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-200">
                        <div className="text-center">
                            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No transactions yet</h3>
                            <p className="text-gray-500 mb-6">Start making purchases to earn loyalty points!</p>
                            <button
                                onClick={() => navigate(PATHS.CUSTOMER_PORTAL)}
                                className="px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                            >
                                View Your Memberships
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900">Transaction History</h2>
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-200">
                            {transactions.map((transaction, index) => (
                                <div key={`${transaction.id}-${index}`} className="p-6 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start gap-4">
                                        {getTransactionIcon(transaction.transactionType)}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <p className="text-sm font-semibold text-gray-900 mb-1">
                                                        {transaction.description}
                                                    </p>
                                                    <p className="text-sm text-gray-500 mb-2">
                                                        {format(new Date(transaction.createdAt), 'MMM dd, yyyy - hh:mm a')}
                                                    </p>                                       
                                                        <p className="text-xs text-gray-400">
                                                            {transaction.relatedPaymentId && !transaction.relatedPaymentId?.toLowerCase().includes('manual') && ( <>Payment ID: </> ) } {transaction.relatedPaymentId}
                                                        </p>
                                 
                                                </div>
                                                <div className="text-right">
                                                    <div className={`text-lg font-bold ${
                                                        transaction.transactionType === 'EARNED' 
                                                            ? 'text-green-600' 
                                                            : 'text-red-600'
                                                    }`}>
                                                        {transaction.transactionType === 'EARNED' ? '+' : '-'}{transaction.points}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {transaction.transactionType === 'EARNED' ? 'Earned' : 'Redeemed'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="p-6 border-t border-gray-200">
                            <PaginationLight page={currentPage} total={totalPages} onChange={(p) => fetchData(p)} />
                        </div>
                    </div>
                )}
                </div>
            </div>
            <CustomerFooter />
        </div>
    );
};

export default PointsHistory;
